import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Box, Typography, CircularProgress, Button, Alert } from "@mui/material";

type Status = "loading" | "success" | "error";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";
  const [status, setStatus] = useState<Status>("loading");
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const navigate = useNavigate();

  const handleResendEmail = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        // Try to parse error message from server
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Verification failed or link has expired.");
      }

      const data: { message?: string } = await response.json();
      setStatus("success");
      setMessage(data.message || "Email verified successfully!");
    } catch (err: any) {
      setStatus("error");
      setMessage(err.message || "Verification failed or link has expired.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link.");
      return;
    }

    const verifyEmail = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/verify-email?token=${token}`
        );

        if (!response.ok) {
          // Try to parse error message from server
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Verification failed or link has expired.");
        }

        const data: { message?: string } = await response.json();
        setStatus("success");
        setMessage(data.message || "Email verified successfully!");
      } catch (err: any) {
        setStatus("error");
        setMessage(err.message || "Verification failed or link has expired.");
      }
      setLoading(false);
    };

    if (loading) return;

    verifyEmail();
  }, [token]);


  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 10,
          p: 4,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Verify Your Email
        </Typography>

        {status === "loading" && <CircularProgress sx={{ mt: 4 }} data-testid="loading-spinner" />}

        {status === "success" && (
          <>
            <Alert severity="success" sx={{ mt: 3 }}>
              {message}
            </Alert>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 4 }}
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ mt: 3 }}>
              {message}
            </Alert>
            <Button
              variant="outlined"
              color="primary"
              sx={{ mt: 4 }}
              onClick={handleResendEmail}
              disabled={loading}
              data-testid="resend-email-btn"
            >
              {
                loading && (
                  <CircularProgress sx={{ mr: 2 }} size={30} />
                )
              }
              <span>Resend Verification Email</span>
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
};

export default VerifyEmail;
